import {
    isValidElement,
    type ReactNode,
    Children
} from "react";
import type {
    TimelineGroupsConstantType,
    TimelineTimeConstantType,
    TimelineItemElementProps,
    TimelineDateValue,
    TimelineTimeTexts,
    TimelineGroupType
} from "./type";
import {
    formatDateTime,
    getIntlLocale
} from "../../helpers/locale";

export const TIMELINE_TICK_DURATION = 60000;

const MINUTE_DURATION = 60000;

const HOUR_DURATION = 3600000;

const DAY_DURATION = 86400000;

const WEEK_DAYS = 7;

const MONTH_DAYS = 30;

const YEAR_DAYS = 365;

const LONG_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
    minute: "2-digit",
    hour: "2-digit",
    year: "numeric",
    day: "numeric",
    month: "long"
};

const RELATIVE_FORMATTERS = new Map<string, Intl.RelativeTimeFormat | null>();

export const getTimelineDate = ({
    value
}: {
    value?: TimelineDateValue;
}): Date | undefined => {
    if(value === undefined) {
        return undefined;
    }

    const date = value instanceof Date ? value : new Date(value);

    return Number.isNaN(date.getTime()) ? undefined : date;
};

export const getTimelineDayKey = ({
    date
}: {
    date: Date;
}) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

const getStartOfDay = ({
    date
}: {
    date: Date;
}) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export const getTimelineDayOffset = ({
    date,
    now
}: {
    now: number;
    date: Date;
}) => Math.round((getStartOfDay({
    date: new Date(now)
}) - getStartOfDay({
    date
})) / DAY_DURATION);

const getRelativeFormatter = ({
    locale,
    style
}: {
    style: "long" | "short";
    locale: string;
}): Intl.RelativeTimeFormat | null => {
    const key = `${locale}|${style}`;

    const cached = RELATIVE_FORMATTERS.get(key);

    if(cached !== undefined) {
        return cached;
    }

    let formatter: Intl.RelativeTimeFormat | null = null;

    try {
        formatter = new Intl.RelativeTimeFormat(getIntlLocale({
            locale
        }), {
            numeric: "always",
            style
        });
    } catch {
        formatter = null;
    }

    RELATIVE_FORMATTERS.set(key, formatter);

    return formatter;
};

const getRelativeTexts = ({
    timeBehaviour,
    localize,
    locale,
    date,
    now
}: Pick<TimelineTimeConstantType, "timeBehaviour" | "localize" | "locale" | "date" | "now">): {
    long?: string;
    text?: string;
} => {
    if(date.getTime() > now + MINUTE_DURATION) {
        return {};
    }

    const difference = Math.max(0, now - date.getTime());

    const dayOffset = getTimelineDayOffset({
        date,
        now
    });

    const short = getRelativeFormatter({
        style: "short",
        locale
    });

    const long = getRelativeFormatter({
        style: "long",
        locale
    });

    const getValue = (value: number, unit: Intl.RelativeTimeFormatUnit) => ({
        long: long?.format(-value, unit),
        text: short?.format(-value, unit)
    });

    if(difference < MINUTE_DURATION) {
        const justNow = localize("timeline-just-now");

        return {
            long: justNow,
            text: justNow
        };
    }

    if(difference < HOUR_DURATION) {
        return getValue(Math.floor(difference / MINUTE_DURATION), "minute");
    }

    if(dayOffset === 0) {
        return getValue(Math.floor(difference / HOUR_DURATION), "hour");
    }

    if(dayOffset === 1) {
        const yesterday = localize("yesterday-at-x", [
            formatDateTime({
                preset: "time",
                locale,
                date
            })
        ]);

        return {
            long: yesterday,
            text: yesterday
        };
    }

    if(dayOffset <= WEEK_DAYS) {
        return getValue(dayOffset, "day");
    }

    if(timeBehaviour === "auto") {
        return {
        };
    }

    if(dayOffset < MONTH_DAYS) {
        return getValue(Math.floor(dayOffset / WEEK_DAYS), "week");
    }

    if(dayOffset < YEAR_DAYS) {
        return getValue(Math.floor(dayOffset / MONTH_DAYS), "month");
    }

    return getValue(Math.floor(dayOffset / YEAR_DAYS), "year");
};

export const getTimelineTimeTexts = ({
    timeBehaviour,
    isDayGrouped,
    localize,
    isToday,
    locale,
    date,
    now
}: TimelineTimeConstantType): TimelineTimeTexts => {
    const absoluteText = formatDateTime({
        preset: "dateTime",
        locale,
        date
    });

    const longText = formatDateTime({
        options: LONG_DATE_TIME_OPTIONS,
        locale,
        date
    });

    if(timeBehaviour === "absolute") {
        return {
            accessibleText: longText,
            text: absoluteText,
            absoluteText
        };
    }

    if(isDayGrouped && !isToday) {
        return {
            accessibleText: longText,
            text: formatDateTime({
                preset: "time",
                locale,
                date
            }),
            absoluteText
        };
    }

    const relative = getRelativeTexts({
        timeBehaviour,
        localize,
        locale,
        date,
        now
    });

    return {
        accessibleText: relative.long ? `${relative.long}, ${longText}` : longText,
        text: relative.text ?? formatDateTime({
            preset: "date",
            locale,
            date
        }),
        absoluteText
    };
};

export const getTimelineDayLabel = ({
    localize,
    locale,
    date,
    now
}: Pick<TimelineTimeConstantType, "localize" | "locale" | "date" | "now">) => {
    const dayOffset = getTimelineDayOffset({
        date,
        now
    });

    if(dayOffset === 0) {
        return localize("timeline-today");
    }

    if(dayOffset === 1) {
        return localize("timeline-yesterday");
    }

    return formatDateTime({
        preset: "date",
        locale,
        date
    });
};

export const getTimelineGroups = ({
    isGrouped,
    children
}: TimelineGroupsConstantType): Array<TimelineGroupType> => {
    const groups: Array<TimelineGroupType> = [];

    Children.toArray(children).forEach((child: ReactNode) => {
        if(!isValidElement<TimelineItemElementProps>(child)) {
            return;
        }

        const date = getTimelineDate({
            value: child.props.time
        });

        const lastGroup = groups[groups.length - 1];

        const dayKey = date ? getTimelineDayKey({
            date
        }) : lastGroup?.dayKey;

        const isNewDay = isGrouped && dayKey !== undefined && dayKey !== lastGroup?.dayKey;

        if(!lastGroup || isNewDay) {
            groups.push({
                key: `${dayKey ?? "all"}-${groups.length}`,
                items: [
                    child
                ],
                dayKey,
                date
            });

            return;
        }

        lastGroup.items.push(child);
    });

    return groups;
};

export const getTimelineNodeText = ({
    node
}: {
    node: ReactNode;
}): string => {
    if(typeof node === "string" || typeof node === "number") {
        return `${node}`;
    }

    if(Array.isArray(node)) {
        return node.map((child: ReactNode) => getTimelineNodeText({
            node: child
        })).join("");
    }

    if(isValidElement<{ children?: ReactNode }>(node)) {
        return getTimelineNodeText({
            node: node.props.children
        });
    }

    return "";
};
